import React, { useState, useEffect, useRef } from 'react';
import { tutorialsAPI } from '../services/api';
import { format } from 'date-fns';

const SECTIONS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'customers', label: 'Customers' },
  { value: 'licenses', label: 'Licenses' },
  { value: 'releases', label: 'Releases' },
  { value: 'api_keys', label: 'API Keys' },
  { value: 'ai_credits', label: 'AI Credits' },
  { value: 'blog', label: 'Blog' },
  { value: 'support', label: 'Support' },
  { value: 'users', label: 'Admin Users' },
  { value: 'activity', label: 'Activity Logs' },
  { value: 'general', label: 'General' },
];

const Tutorials = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filterSection, setFilterSection] = useState('');
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  const emptyForm = {
    title: '',
    description: '',
    section: 'general',
    video_url: '',
    thumbnail_url: '',
    duration: '',
    sort_order: 0,
    status: 'draft',
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    setLoading(true);
    try {
      const response = await tutorialsAPI.list();
      setTutorials(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch tutorials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await tutorialsAPI.uploadVideo(formData, (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      });
      setForm({ ...form, video_url: response.data.url });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Video upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await tutorialsAPI.uploadThumbnail(formData);
      setForm({ ...form, thumbnail_url: response.data.url });
    } catch (err) {
      console.error('Thumbnail upload failed:', err);
      alert('Thumbnail upload failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.video_url) {
      alert('Please upload a video or provide a video URL.');
      return;
    }
    try {
      await tutorialsAPI.create(form);
      setShowCreateModal(false);
      setForm(emptyForm);
      fetchTutorials();
    } catch (err) {
      console.error('Failed to create tutorial:', err);
      alert('Failed to create tutorial: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedTutorial) return;
    try {
      await tutorialsAPI.update(selectedTutorial.id, form);
      setShowEditModal(false);
      setSelectedTutorial(null);
      setForm(emptyForm);
      fetchTutorials();
    } catch (err) {
      console.error('Failed to update tutorial:', err);
      alert('Failed to update tutorial: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tutorial?')) return;
    try {
      await tutorialsAPI.delete(id);
      fetchTutorials();
    } catch (err) {
      console.error('Failed to delete tutorial:', err);
      alert('Failed to delete tutorial.');
    }
  };

  const openEditModal = (tutorial) => {
    setSelectedTutorial(tutorial);
    setForm({
      title: tutorial.title,
      description: tutorial.description,
      section: tutorial.section,
      video_url: tutorial.video_url,
      thumbnail_url: tutorial.thumbnail_url || '',
      duration: tutorial.duration || '',
      sort_order: tutorial.sort_order,
      status: tutorial.status,
    });
    setShowEditModal(true);
  };

  const openVideoModal = (tutorial) => {
    setSelectedTutorial(tutorial);
    setShowVideoModal(true);
  };

  const filteredTutorials = filterSection
    ? tutorials.filter((t) => t.section === filterSection)
    : tutorials;

  // Group tutorials by section
  const groupedTutorials = filteredTutorials.reduce((acc, tutorial) => {
    const section = tutorial.section;
    if (!acc[section]) acc[section] = [];
    acc[section].push(tutorial);
    return acc;
  }, {});

  const getSectionLabel = (value) => {
    return SECTIONS.find((s) => s.value === value)?.label || value;
  };

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div className="modal-body">
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., How to Create a License"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-input"
            rows={3}
            placeholder="Brief description of what this tutorial covers..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Section *</label>
            <select
              className="form-input"
              value={form.section}
              onChange={(e) => setForm({ ...form, section: e.target.value })}
            >
              {SECTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-input"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Duration</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., 2:30"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Sort Order</label>
            <input
              type="number"
              className="form-input"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>

        {/* Video Upload */}
        <div className="form-group">
          <label className="form-label">Video *</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Video URL (upload or paste URL)"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              style={{ flex: 1 }}
            />
            <input
              type="file"
              accept="video/mp4,video/webm,video/ogg"
              ref={videoInputRef}
              style={{ display: 'none' }}
              onChange={handleVideoUpload}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploading}
              style={{ whiteSpace: 'nowrap' }}
            >
              {uploading ? `${uploadProgress}%` : 'Upload Video'}
            </button>
          </div>
          {uploading && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ background: 'var(--gray-200)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                <div style={{ background: 'var(--primary)', height: '100%', width: `${uploadProgress}%`, transition: 'width 0.2s' }}></div>
              </div>
            </div>
          )}
          {form.video_url && !uploading && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--success)' }}>
              Video ready
            </div>
          )}
        </div>

        {/* Thumbnail Upload */}
        <div className="form-group">
          <label className="form-label">Thumbnail</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Thumbnail URL (optional)"
              value={form.thumbnail_url}
              onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
              style={{ flex: 1 }}
            />
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              ref={thumbnailInputRef}
              style={{ display: 'none' }}
              onChange={handleThumbnailUpload}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => thumbnailInputRef.current?.click()}
              style={{ whiteSpace: 'nowrap' }}
            >
              Upload Image
            </button>
          </div>
          {form.thumbnail_url && (
            <div style={{ marginTop: '0.5rem' }}>
              <img src={form.thumbnail_url} alt="Thumbnail" style={{ height: '60px', borderRadius: '4px' }} />
            </div>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => { setShowCreateModal(false); setShowEditModal(false); setForm(emptyForm); }}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={uploading}>
          {submitLabel}
        </button>
      </div>
    </form>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Video Tutorials</h1>
          <p className="page-description">Manage video tutorials for each portal process</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setShowCreateModal(true); }}>
          Add Tutorial
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>{tutorials.length}</div>
          <div style={{ color: 'var(--text-secondary)' }}>Total Tutorials</div>
        </div>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>
            {tutorials.filter((t) => t.status === 'published').length}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>Published</div>
        </div>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning)' }}>
            {Object.keys(groupedTutorials).length}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>Sections Covered</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          className={`btn btn-sm ${!filterSection ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterSection('')}
        >
          All
        </button>
        {SECTIONS.map((s) => (
          <button
            key={s.value}
            className={`btn btn-sm ${filterSection === s.value ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilterSection(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : filteredTutorials.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            {filterSection ? 'No tutorials in this section.' : 'No tutorials yet. Add your first tutorial to get started.'}
          </div>
        ) : (
          Object.entries(groupedTutorials).map(([section, sectionTutorials]) => (
            <div key={section} style={{ marginBottom: '2rem' }}>
              <h3 style={{ padding: '0.75rem 1.5rem', margin: 0, background: 'var(--gray-100)', borderBottom: '1px solid var(--gray-200)', fontSize: '0.95rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {getSectionLabel(section)} ({sectionTutorials.length})
              </h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Tutorial</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionTutorials.map((tutorial) => (
                      <tr key={tutorial.id}>
                        <td style={{ color: 'var(--text-secondary)' }}>{tutorial.sort_order}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {tutorial.thumbnail_url ? (
                              <img
                                src={tutorial.thumbnail_url}
                                alt=""
                                style={{ width: '64px', height: '36px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={() => openVideoModal(tutorial)}
                              />
                            ) : (
                              <div
                                style={{ width: '64px', height: '36px', background: 'var(--gray-200)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.2rem' }}
                                onClick={() => openVideoModal(tutorial)}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => openVideoModal(tutorial)}>
                                {tutorial.title}
                              </div>
                              {tutorial.description && (
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '2px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {tutorial.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{tutorial.duration || '-'}</td>
                        <td>
                          {tutorial.status === 'published' ? (
                            <span className="badge badge-success">Published</span>
                          ) : (
                            <span className="badge badge-gray">Draft</span>
                          )}
                        </td>
                        <td>{format(new Date(tutorial.created_at), 'MMM d, yyyy')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button className="btn btn-sm btn-primary" onClick={() => openVideoModal(tutorial)}>
                              Play
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(tutorial)}>
                              Edit
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{ background: 'var(--danger)', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                              onClick={() => handleDelete(tutorial.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Tutorial</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            {renderForm(handleCreate, 'Add Tutorial')}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Tutorial</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            {renderForm(handleEdit, 'Save Changes')}
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoModal && selectedTutorial && (
        <div className="modal-overlay" onClick={() => { setShowVideoModal(false); setSelectedTutorial(null); }}>
          <div
            className="modal"
            style={{ maxWidth: '900px', width: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{selectedTutorial.title}</h2>
              <button className="modal-close" onClick={() => { setShowVideoModal(false); setSelectedTutorial(null); }}>&times;</button>
            </div>
            <div style={{ padding: '0' }}>
              <video
                controls
                autoPlay
                style={{ width: '100%', maxHeight: '70vh', background: '#000' }}
                src={selectedTutorial.video_url}
              >
                Your browser does not support the video tag.
              </video>
              {selectedTutorial.description && (
                <div style={{ padding: '1rem 1.5rem', color: 'var(--text-secondary)', borderTop: '1px solid var(--gray-200)' }}>
                  {selectedTutorial.description}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--text-secondary);
          padding: 0;
          line-height: 1;
        }
        .modal-close:hover {
          color: var(--text-primary);
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--gray-200);
        }
      `}</style>
    </div>
  );
};

export default Tutorials;
